import httpClient from './httpClient';

class CategoriesService {
  // Get all categories
  async getCategories() {
    try {
      console.log('CategoriesService: Making request to /api/admin/categories');
      const response = await httpClient.get('/api/admin/categories?level=root');
      console.log('CategoriesService: Raw response from httpClient:', response);
      return response;
    } catch (error) {
      console.error('CategoriesService: Error in getCategories:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء جلب الفئات'
      };
    }
  }

  // Get all categories for dropdowns and filters (without level restriction)
  async getAllCategories() {
    try {
      console.log('CategoriesService: Making request to /api/admin/categories for all categories');
      const response = await httpClient.get('/api/admin/categories');
      console.log('CategoriesService: Raw response from httpClient for all categories:', response);
      return response;
    } catch (error) {
      console.error('CategoriesService: Error in getAllCategories:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء جلب جميع الفئات'
      };
    }
  }

  // Get all categories formatted for filter dropdowns with hierarchical structure
  async getCategoriesForFilter() {
    try {
      console.log('CategoriesService: Making request to /api/admin/categories for filter dropdown');
      
      // Try different endpoints to find the one that works
      let response = await httpClient.get('/api/admin/categories');
      console.log('CategoriesService: Raw response from httpClient for filter:', response);
      
      // If the first endpoint doesn't work, try alternative endpoints
      if (!response.success || !response.data) {
        console.log('CategoriesService: First endpoint failed, trying alternative...');
        response = await httpClient.get('/api/admin/categories?level=all');
        console.log('CategoriesService: Alternative endpoint response:', response);
      }
      
      if (response.success && response.data) {
        // Handle the API response structure: { data: [...], status: 1, message: "..." }
        if (response.data.status === 1) {
          const categoriesData = response.data.data || response.data || [];
          console.log('CategoriesService: Categories data for filter:', categoriesData);
          
          // Return the raw hierarchical data - let the UI component handle formatting
          return {
            success: true,
            data: categoriesData,
            message: response.data.message
          };
        } else {
          return {
            success: false,
            data: null,
            message: response.data.message || 'حدث خطأ في الخادم'
          };
        }
      }
      
      return response;
    } catch (error) {
      console.error('CategoriesService: Error in getCategoriesForFilter:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء جلب الفئات للفلتر'
      };
    }
  }

  // Helper method to format categories for dropdown display
  formatCategoriesForDropdown(categories, level = 0) {
    if (!Array.isArray(categories)) return [];
    
    let formatted = [];
    
    categories.forEach(category => {
      // Add current category with proper indentation
      const prefix = '─ '.repeat(level);
      formatted.push({
        id: category.id,
        name: `${prefix}${category.name}`,
        original_name: category.name,
        level: level,
        parent_category_id: category.parent_category_id
      });
      
      // Recursively add children if they exist
      if (category.children && Array.isArray(category.children) && category.children.length > 0) {
        formatted = formatted.concat(this.formatCategoriesForDropdown(category.children, level + 1));
      }
    });
    
    return formatted;
  }

  // Fallback method to format flat categories for dropdown
  formatFlatCategoriesForDropdown(categories) {
    if (!Array.isArray(categories)) return [];
    
    return categories.map(category => ({
      id: category.id,
      name: category.name,
      original_name: category.name,
      level: 0,
      parent_category_id: category.parent_category_id
    }));
  }

  // Create new category
  async createCategory(categoryData) {
    try {
      console.log('CategoriesService: Received categoryData:', categoryData);
      
      let requestData;
      
      // Check if we have an image to upload
      if (categoryData.image && categoryData.image instanceof File) {
        // Send as FormData for image upload
        const formData = new FormData();
        formData.append('en_name', categoryData.en_name);
        formData.append('ar_name', categoryData.ar_name);
        formData.append('image', categoryData.image);
        
        if (categoryData.parent_category_id && 
            categoryData.parent_category_id !== "" && 
            categoryData.parent_category_id !== undefined &&
            categoryData.parent_category_id !== null) {
          formData.append('parent_category_id', categoryData.parent_category_id);
        }
        
        requestData = formData;
        console.log('CategoriesService: Sending as FormData with image');
        console.log('CategoriesService: Image file details:', {
          name: categoryData.image.name,
          size: categoryData.image.size,
          type: categoryData.image.type
        });
        
        // Log FormData contents for debugging
        for (let [key, value] of formData.entries()) {
          console.log(`CategoriesService: FormData entry - ${key}:`, value);
        }
      } else {
        // Send as JSON for text-only data
        requestData = {
          en_name: categoryData.en_name,
          ar_name: categoryData.ar_name,
          parent_category_id: categoryData.parent_category_id || null
        };
        console.log('CategoriesService: Sending as JSON without image');
      }

      // If creating a child category, append parent_id to the endpoint
      let endpoint = '/api/admin/categories';
      if (categoryData.parent_category_id && 
          categoryData.parent_category_id !== "" && 
          categoryData.parent_category_id !== undefined &&
          categoryData.parent_category_id !== null) {
        endpoint += `?parent_id=${categoryData.parent_category_id}`;
      }
      // For root categories, no query parameter needed

      console.log('CategoriesService: Final endpoint:', endpoint);
      console.log('CategoriesService: Request data type:', requestData instanceof FormData ? 'FormData' : 'JSON');
      console.log('CategoriesService: Request data:', requestData);

      const response = await httpClient.post(endpoint, requestData);
      return response;
    } catch (error) {
      console.error('Error creating category:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء إنشاء الفئة'
      };
    }
  }

  // Update category
  async updateCategory(id, categoryData) {
    try {
      let requestData;
      
      // Check if we have an image to upload
      if (categoryData.image && categoryData.image instanceof File) {
        // Send as FormData for image upload
        const formData = new FormData();
        formData.append('en_name', categoryData.en_name);
        formData.append('ar_name', categoryData.ar_name);
        formData.append('image', categoryData.image);
        formData.append('_method', 'PUT'); // For Laravel to handle PUT requests
        
        if (categoryData.parent_category_id && 
            categoryData.parent_category_id !== "" && 
            categoryData.parent_category_id !== undefined &&
            categoryData.parent_category_id !== null) {
          formData.append('parent_category_id', categoryData.parent_category_id);
        }
        
        requestData = formData;
        console.log('CategoriesService: Updating with FormData and image');
        console.log('CategoriesService: Image file details:', {
          name: categoryData.image.name,
          size: categoryData.image.size,
          type: categoryData.image.type
        });
        
        // Log FormData contents for debugging
        for (let [key, value] of formData.entries()) {
          console.log(`CategoriesService: FormData entry - ${key}:`, value);
        }
      } else {
        // Send as JSON for text-only data
        requestData = {
          en_name: categoryData.en_name,
          ar_name: categoryData.ar_name,
          parent_category_id: categoryData.parent_category_id || null,
          _method: 'PUT' // For Laravel to handle PUT requests
        };
        console.log('CategoriesService: Updating with JSON without image');
      }

      console.log('CategoriesService: Update request data type:', requestData instanceof FormData ? 'FormData' : 'JSON');
      console.log('CategoriesService: Update request data:', requestData);

      const response = await httpClient.post(`/api/admin/categories/${id}`, requestData);
      return response;
    } catch (error) {
      console.error('Error updating category:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء تحديث الفئة'
      };
    }
  }

  // Get categories for tree structure
  async getCategoriesTree() {
    try {
      const response = await httpClient.get('/api/admin/categories?level=root');
      return response;
    } catch (error) {
      console.error('Error fetching categories tree:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء جلب الفئات'
      };
    }
  }

  // Get categories by parent ID
  async getCategoriesByParent(parentId = null) {
    try {
      const endpoint = parentId ? `/api/admin/categories?parent_id=${parentId}` : '/api/admin/categories?level=root';
      const response = await httpClient.get(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching categories by parent:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء جلب الفئات'
      };
    }
  }

  // Delete category
  async deleteCategory(id) {
    try {
      const response = await httpClient.delete(`/api/admin/categories/${id}`);
      return response;
    } catch (error) {
      console.error('Error deleting category:', error);
      return {
        success: false,
        data: null,
        message: 'حدث خطأ أثناء حذف الفئة'
      };
    }
  }
}

export default new CategoriesService();
